import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { qk, type DocumentDto, type Period } from '@immoaktas/api-client';
import { enumLabel } from '@immoaktas/i18n';
import { COST_CATEGORIES, RULES, daysInclusive, euro, maxDate, minDate, num, type CostCategory } from '@immoaktas/shared';
import { api, role } from '../../../lib/api';
import { can, useAct } from '../../../lib/hooks';
import { shortRange } from '../../../lib/format';
import { BackLink, Button, FileBadge, Icon, PhaseSteps, Pill, ProgressBar, SelectInput, Sheet, Spinner, type PillTone } from '../../../ui';
import { useAiConsentGate } from './AiConsent';
import { useUploadSheet, type UploadItem } from './uploadStore';

const PHASES = ['drop', 'upload', 'read', 'fields', 'year', 'checklist'] as const;
const FINAL = new Set(['needs_review', 'ready', 'failed', 'duplicate', 'confirmed']);
const reduceMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Mounted once in the shell: renders the sheet and opens it for links with `?upload=1` on any route. */
export function UploadSheetHost() {
  const [sp, setSp] = useSearchParams();
  const { open, show } = useUploadSheet();
  useEffect(() => {
    if (!sp.get('upload')) return;
    const n = new URLSearchParams(sp);
    n.delete('upload');
    setSp(n, { replace: true });
    show({ propertyId: sp.get('propertyId') ?? undefined });
  }, [sp, setSp, show]);
  return open ? <UploadSheet /> : null;
}

/** Shows the target phase one step at a time so each step is visible (docs/10: motion explains cause → effect). */
function usePacedPhase(target: number, key: string) {
  const [shown, setShown] = useState(target);
  const last = useRef(key);
  useEffect(() => {
    if (last.current !== key) { last.current = key; setShown(target); return; }
    if (shown === target) return;
    if (reduceMotion() || target < shown) { setShown(target); return; }
    const h = setTimeout(() => setShown((s) => Math.min(target, s + 1)), 450);
    return () => clearTimeout(h);
  }, [target, shown, key]);
  return shown;
}

function UploadSheet() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { ctx, items, focus, hide, add, update, setFocus, clearDone } = useUploadSheet();
  const aiGate = useAiConsentGate();
  const props = useQuery({ queryKey: qk.properties, queryFn: () => api.properties.list() });
  const [propertyId, setPropertyId] = useState(ctx.propertyId ?? '');
  useEffect(() => { if (!propertyId && props.data?.length === 1) setPropertyId(props.data[0]!.id); }, [props.data, propertyId]);
  // Poll every document that is still being analysed.
  const docs = useQueries({ queries: items.filter((i) => i.documentId).map((i) => ({ queryKey: qk.document(i.documentId!), queryFn: () => api.documents.get(i.documentId!), refetchInterval: (q: { state: { data?: DocumentDto } }) => (q.state.data && FINAL.has(q.state.data.status) ? false : 2000) })) });
  const docOf = (i: UploadItem) => docs.find((d) => d.data?.id === i.documentId)?.data;
  useEffect(() => {
    for (const i of items) {
      const d = docOf(i);
      if (i.state === 'processing' && d && FINAL.has(d.status)) {
        update(i.key, { state: d.status === 'failed' ? 'error' : d.status === 'duplicate' ? 'duplicate' : 'analyzed' });
        void qc.invalidateQueries({ queryKey: ['documents'] });
      }
    }
  }); // runs after every poll result
  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    // Receipts go to the AI only with consent; "no" still uploads for manual entry, closing cancels (AiConsent).
    if ((await aiGate.ensure()) === 'cancelled') return;
    add(files, propertyId || undefined, ctx.hint);
  };
  const pending = useRef(ctx.files);
  useEffect(() => { const f = pending.current; pending.current = undefined; if (f?.length) void addFiles(f); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const open = items.filter((i) => i.state !== 'confirmed');
  const cur = items.find((i) => i.key === focus && i.state !== 'confirmed') ?? open[0];
  const doc = cur ? docOf(cur) : undefined;
  // The receipt's property (set on upload or recognised by the AI from the vendor) decides period and checklist.
  const pid = doc?.propertyId ?? cur?.propertyId ?? propertyId;
  const property = props.data?.find((p) => p.id === pid);
  const periods = useQuery({ queryKey: qk.periods({ propertyId: pid }), queryFn: () => api.billing.periods({ propertyId: pid }), enabled: !!pid });
  const period = pickPeriod(periods.data ?? [], ctx.periodId, doc);
  const checklist = useQuery({ queryKey: qk.checklist(period?.id ?? ''), queryFn: () => api.billing.checklist(period!.id), enabled: !!period });
  const target = !cur ? 0 : cur.state === 'waiting' || cur.state === 'uploading' ? 1 : cur.state === 'processing' ? 2 : cur.state === 'error' && !cur.documentId ? 1 : !doc ? 2 : doc.aiSkipped ? 2 : doc.fields ? (period && doc.fields.serviceFrom ? (checklist.isFetching ? 4 : 5) : 3) : 2;
  const phase = usePacedPhase(target, cur?.key ?? '');
  const busy = items.some((i) => i.state === 'uploading' || i.state === 'waiting');
  return (
    <Sheet open onClose={hide} label={t('uploadSheet.title')}>
      <div className="page-stack upload-sheet">
        <header className="page-head">
          <div className="titles">
            <span className="eyebrow"><button type="button" className="back-link as-button" onClick={hide}><Icon name="arrowRight" size={15} strokeWidth={1.8} className="flip" /><span>{t('nav.documents')}</span></button></span>
            <h1>{t('uploadSheet.title')}</h1>
            <div className="sub">{[property?.name, period ? t('periods.cardTitle', { year: periodYearOf(period) }) : null, ctx.hint?.category ? t('uploadSheet.forCategory', { category: enumLabel(t, 'costCategory', ctx.hint.category) }) : t('uploadSheet.sources')].filter(Boolean).join(' · ')}</div>
          </div>
          <div className="actions">
            {(props.data?.length ?? 0) > 1 && <SelectInput label={t('fields.property')} labelHidden value={propertyId} onChange={(e) => setPropertyId(e.target.value)} options={[{ value: '', label: t('uploadSheet.noProperty') }, ...(props.data ?? []).map((p) => ({ value: p.id, label: p.name }))]} />}
            <Button variant="secondary" icon="refresh" disabled={busy || !items.length} onClick={clearDone}>{t('uploadSheet.restart')}</Button>
          </div>
        </header>
        <PhaseSteps label={t('uploadSheet.phasesLabel')} steps={PHASES.map((p) => t(`uploadSheet.phase.${p}`))} current={cur && phase === 5 && checklist.data ? 6 : phase} />
        <div className="split">
          <section className="card roomy split-main upload-main" aria-label={t('uploadSheet.current')} aria-live="polite">
            <Current item={cur} doc={doc} phase={phase} index={cur ? open.indexOf(cur) + 1 : 0} total={open.length} period={period}
              onFiles={(f) => void addFiles(f)} onEdit={() => { if (cur?.documentId) { hide(); nav(`/belege/${cur.documentId}`); } }}
              onConfirmed={() => { update(cur!.key, { state: 'confirmed' }); setFocus(open.find((i) => i.key !== cur!.key)?.key ?? null); }} />
          </section>
          <aside className="split-side">
            {period && <ChecklistMini period={period} updated={phase >= 5} />}
            <Queue items={items.filter((i) => i.key !== cur?.key)} docOf={docOf} onPick={(k) => setFocus(k)} onAdd={(f) => void addFiles(f)} />
          </aside>
        </div>
      </div>
      {aiGate.dialog}
    </Sheet>
  );
}

const periodYearOf = (p: Pick<Period, 'from' | 'to'>) => (p.from.slice(0, 4) === p.to.slice(0, 4) ? p.from.slice(0, 4) : `${p.from.slice(0, 4)}/${p.to.slice(2, 4)}`);

/** The billing period the receipt belongs to: the one with most service days, else the given or the open one. */
function pickPeriod(list: Period[], periodId?: string, doc?: DocumentDto) {
  const from = doc?.fields?.serviceFrom as string | undefined;
  const to = doc?.fields?.serviceTo as string | undefined;
  if (from && to) {
    const best = list.map((p) => ({ p, days: from <= p.to && to >= p.from ? daysInclusive(maxDate(from, p.from), minDate(to, p.to)) : 0 })).sort((a, b) => b.days - a.days)[0];
    if (best && best.days > 0) return best.p;
  }
  return list.find((p) => p.id === periodId) ?? list.find((p) => ['collecting', 'ready', 'draft'].includes(p.status));
}

function Current({ item, doc, phase, index, total, period, onFiles, onEdit, onConfirmed }: { item?: UploadItem; doc?: DocumentDto; phase: number; index: number; total: number; period?: Period; onFiles: (f: File[]) => void; onEdit: () => void; onConfirmed: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const thumb = useMemo(() => (item && item.mime.startsWith('image/') ? URL.createObjectURL(item.file) : null), [item]);
  useEffect(() => () => { if (thumb) URL.revokeObjectURL(thumb); }, [thumb]);
  const f = doc?.fields ?? {};
  // A line without a recognised cost type takes the one the receipt was uploaded for (Umlage-Assistent).
  const hintCategory = doc?.uploadHint?.category ?? item?.hint?.category;
  type Line = { text?: string; amountCents?: number; suggestedCategory?: string; apportionable?: string; quantity?: number; unit?: string };
  const lines: Line[] = (Array.isArray(f.lines) ? (f.lines as Line[]) : []).map((l) => (l.suggestedCategory || !hintCategory ? l : { ...l, suggestedCategory: hintCategory }));
  const cats = [...new Set(lines.map((l) => l.suggestedCategory).filter((c): c is CostCategory => !!c && (COST_CATEGORIES as readonly string[]).includes(c)))];
  const conf = Object.values(doc?.extraction?.confidence ?? {});
  const minConf = conf.length ? Math.min(...conf) : null;
  // Quick confirm only for clear yes/no lines: partial needs its non-apportionable amount, unknown needs a decision (full review).
  const canConfirm = !!doc && doc.status === 'ready' && !!doc.propertyId && !!f.serviceFrom && !!f.serviceTo && lines.length > 0 && lines.every((l) => l.suggestedCategory && (COST_CATEGORIES as readonly string[]).includes(l.suggestedCategory) && l.amountCents != null && (l.apportionable === 'yes' || l.apportionable === 'no')) && can(role(), 'write');
  const confirm = useAct(() => api.documents.confirm(doc!.id, {
    propertyId: doc!.propertyId!, vendor: doc!.vendor ?? f.vendor ?? undefined, invoiceNo: doc!.invoiceNo ?? f.invoiceNo ?? undefined,
    items: lines.map((l) => ({ category: l.suggestedCategory as CostCategory, label: l.text ?? '', amountCents: l.amountCents!, serviceFrom: f.serviceFrom, serviceTo: f.serviceTo, apportionable: l.apportionable as 'yes' | 'no' })),
  }), { ok: t('uploadSheet.confirmed'), invalidate: [['documents'], ['document'], ['costs'], ['property'], ['period'], ['periods']], onSuccess: onConfirmed });
  useEffect(() => { if (phase >= 4 && period) void qc.invalidateQueries({ queryKey: qk.checklist(period.id) }); }, [phase >= 4, period?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const badge: [string, PillTone] = !item ? [t('uploadSheet.state.waitFile'), 'neutral'] : item.state === 'error' ? [t('uploadSheet.state.error'), 'err'] : item.state === 'duplicate' ? [t('uploadSheet.state.duplicate'), 'neutral'] : doc?.aiSkipped ? [t('uploadSheet.state.manual'), 'manual'] : [[t('uploadSheet.state.waitFile'), t('uploadSheet.state.uploading'), t('uploadSheet.state.reading'), t('uploadSheet.state.fields'), t('uploadSheet.state.assigned'), doc?.status === 'ready' ? t('uploadSheet.state.done') : t('uploadSheet.state.check')][phase]!, phase >= 4 ? 'ok' : phase >= 1 ? 'brand' : 'neutral'];
  const share = period && f.serviceFrom && f.serviceTo ? overlap(period, f.serviceFrom, f.serviceTo) : null;
  const pick = (files: File[]) => files.length && onFiles(files);
  return (
    <div className="stack loose">
      <div className="row between">
        <span className="up-title">
          <span className="up-eyebrow">{item ? t('uploadSheet.counter', { index, total }) : t('uploadSheet.noFileYet')}</span>
          <strong>{item?.name ?? t('uploadSheet.dropTitle')}</strong>
        </span>
        <Pill tone={badge[1]} size="md">{badge[0]}</Pill>
      </div>
      <div className="up-grid">
        <div className="stack tight">
          <div className={`up-drop ${!item ? 'waiting' : ''} ${over ? 'over' : ''}`} role={!item ? 'button' : undefined} tabIndex={!item ? 0 : undefined}
            onClick={() => !item && input.current?.click()} onKeyDown={(e) => { if (!item && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); input.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); pick([...e.dataTransfer.files]); }}>
            <input ref={input} type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.webp" onChange={(e) => { const x = [...(e.target.files ?? [])]; e.target.value = ''; pick(x); }} />
            {!item ? (
              <span className="up-hint"><Icon name="upload" size={28} strokeWidth={1.8} /><strong>{t('uploadSheet.dropHere')}</strong><span>{t('uploadSheet.dropFormats', { mb: Math.round(RULES.uploadMaxBytes / 1024 / 1024) })}</span></span>
            ) : (
              <div className={`up-paper ${phase === 1 ? 'fly' : ''}`} role="img" aria-label={item.name}>
                {thumb ? <img src={thumb} alt="" /> : (
                  <>
                    <span className="pp-head"><strong>{doc?.vendor ?? f.vendor ?? item.name.replace(/\.[^.]+$/, '')}</strong><span /></span>
                    <span className="pp-bar w70" /><span className="pp-bar w55" />
                    {f.serviceFrom && <span className="pp-small">{shortRange(f.serviceFrom, f.serviceTo ?? f.serviceFrom)}</span>}
                    <span className="pp-lines">{lines.slice(0, 3).map((l, i) => <span key={i}><span>{l.text}</span><span>{euro(l.amountCents)}</span></span>)}</span>
                    {f.totalGrossCents != null && <span className="pp-total"><span>{t('ui.total')}</span><span>{euro(f.totalGrossCents)}</span></span>}
                    <span className="pp-bar w80 soft" /><span className="pp-bar w60 soft" />
                  </>
                )}
                {phase === 2 && !doc?.aiSkipped && <><span className="scan-line" aria-hidden /><span className="scan-tint" aria-hidden /></>}
              </div>
            )}
          </div>
          {item?.state === 'uploading' && <div className="up-progress"><span className="row between small"><span>{t('uploadSheet.uploadingSize', { size: num(item.size / 1024 / 1024, 1) })}</span><strong>{Math.round(item.progress * 100)} %</strong></span><ProgressBar value={item.progress} thin label={t('uploadSheet.phase.upload')} /></div>}
          {phase === 2 && item?.state === 'processing' && !doc?.aiSkipped && <span className="up-reading"><Spinner size={16} />{t('uploadSheet.reading')}<span className="dots" aria-hidden><span>.</span><span>.</span><span>.</span></span></span>}
          {item?.state === 'error' && <div className="banner err small" role="alert">{item.error ?? (doc?.status === 'failed' ? t('uploadSheet.failed') : t('errors:generic'))}</div>}
          {item?.state === 'duplicate' && <div className="banner warn small">{t('uploadSheet.duplicate')} {item.documentId && <Link to={`/belege/${doc?.duplicateOf ?? item.documentId}`}>{t('uploadSheet.openExisting')}</Link>}</div>}
          {doc?.aiSkipped && <div className="banner info small">{doc.aiSkipped === 'no_consent' ? t('common:aiConsent.manualBanner') : t('uploadSheet.manual')}</div>}
        </div>
        <div className="stack tight">
          <span className="up-label">{t('uploadSheet.recognized')}</span>
          <dl className="up-fields stagger">
            {phase < 3 || !doc?.fields ? <p className="muted small">{t('uploadSheet.fieldsLater')}</p> : (
              <>
                <div><dt>{t('receiptsPage.vendor')}</dt><dd>{f.vendor ?? '–'}</dd></div>
                <div><dt>{t('fields.period')}</dt><dd>{f.serviceFrom && f.serviceTo ? shortRange(f.serviceFrom, f.serviceTo) : '–'}</dd></div>
                <div><dt>{t('fields.amount')}</dt><dd className="big">{euro(f.totalGrossCents)}</dd></div>
                <div><dt>{t('fields.costCategory')}</dt><dd>{cats.map((c) => enumLabel(t, 'costCategory', c)).join(', ') || '–'}</dd></div>
                {minConf != null && <div><dt>{t('uploadSheet.confidence')}</dt><dd className={minConf >= RULES.aiConfidenceThreshold ? 'ok-text' : 'warn-text'}>{t('uploadSheet.confidenceValue', { pct: Math.round(minConf * 100) })}</dd></div>}
              </>
            )}
          </dl>
          {phase >= 4 && period && share && (
            <div className="up-year fade-in">
              <span className="up-label brand">{t('uploadSheet.yearTitle')}</span>
              <span className="split-bar sm"><span className="sb-part primary" style={{ width: `${Math.round((share.days / share.serviceDays) * 100)}%` }} /></span>
              <span className="row between small"><span><strong>{periodYearOf(period)}</strong> · {t('uploadSheet.yearDays', { days: share.days, total: share.serviceDays })}</span>{share.days === share.serviceDays && <strong>{euro(f.totalGrossCents)}</strong>}</span>
            </div>
          )}
        </div>
      </div>
      <div className="up-actions">
        {item?.documentId && <Button variant="secondary" size="lg" onClick={onEdit}>{t('uploadSheet.edit')}</Button>}
        <Button size="lg" disabled={!canConfirm || phase < 4} loading={confirm.isPending} title={!canConfirm && item?.documentId ? t('uploadSheet.confirmHint') : undefined} onClick={() => confirm.mutate(undefined)}>{t('uploadSheet.confirmNext')}</Button>
      </div>
      {item?.documentId && !canConfirm && phase >= 3 && <p className="small muted up-note">{t('uploadSheet.confirmHint')}</p>}
    </div>
  );
}

function overlap(p: Period, from: string, to: string) {
  const days = from <= p.to && to >= p.from ? daysInclusive(maxDate(from, p.from), minDate(to, p.to)) : 0;
  return days ? { days, serviceDays: daysInclusive(from, to) } : null;
}

/** Checklist of the period the receipt belongs to; updates after the receipt is assigned (design/BelegUpload). */
function ChecklistMini({ period, updated }: { period: Period; updated: boolean }) {
  const { t } = useTranslation();
  const cl = useQuery({ queryKey: qk.checklist(period.id), queryFn: () => api.billing.checklist(period.id) });
  const costs = cl.data?.items.filter((i) => i.id.startsWith('cost:')) ?? [];
  const missing = costs.filter((i) => i.status !== 'ok');
  const ok = costs.length - missing.length;
  return (
    <section className={`card up-check ${updated && missing.length ? 'warn' : ''} ${updated && cl.data && !missing.length ? 'ok' : ''}`} aria-label={t('uploadSheet.checklist', { year: periodYearOf(period) })}>
      <div className="row between">
        <h3 className="card-title">{t('uploadSheet.checklist', { year: periodYearOf(period) })}</h3>
        {cl.data && <Pill tone={missing.length ? 'warn' : 'ok'} dot>{missing.length ? t('uploadSheet.missing', { count: missing.length }) : t('uploadSheet.complete')}</Pill>}
      </div>
      <ProgressBar value={costs.length ? ok / costs.length : 0} tone={missing.length ? 'warn' : 'ok'} thin label={t('uploadSheet.checklist', { year: periodYearOf(period) })} />
      {missing.slice(0, 3).map((i) => <span key={i.id} className="row between small"><span>{enumLabel(t, 'costCategory', i.id.slice(5))}</span><strong className="warn-text">{t('uploadSheet.missingOne')}</strong></span>)}
      {ok > 0 && <span className="row between small muted"><span>{t('uploadSheet.okMore', { count: ok })}</span><span>{t('uploadSheet.there')}</span></span>}
      <Link className="text-link small" to={`/abrechnungen/${period.id}/checkliste`} onClick={() => useUploadSheet.getState().hide()}>{t('uploadSheet.toChecklist')}<Icon name="arrowRight" size={14} strokeWidth={2} /></Link>
    </section>
  );
}

function Queue({ items, docOf, onPick, onAdd }: { items: UploadItem[]; docOf: (i: UploadItem) => DocumentDto | undefined; onPick: (key: string) => void; onAdd: (f: File[]) => void }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const waiting = items.filter((i) => i.state !== 'confirmed').length;
  return (
    <section className="card up-queue" aria-label={t('uploadSheet.queue')}>
      <div className="row between">
        <h3 className="card-title">{t('uploadSheet.queue')}</h3>
        <span className="small muted">{t('uploadSheet.more', { count: waiting })}</span>
      </div>
      {items.length === 0 ? <p className="small muted">{t('uploadSheet.queueEmpty')}</p> : (
        <ul className="up-list">
          {items.map((i) => {
            const d = docOf(i);
            const [label, tone]: [string, PillTone] = i.state === 'waiting' ? [t('uploadSheet.q.waiting'), 'neutral'] : i.state === 'uploading' ? [t('uploadSheet.q.uploading', { pct: Math.round(i.progress * 100) }), 'brand'] : i.state === 'processing' ? [t('uploadSheet.q.analysing'), 'brand'] : i.state === 'error' ? [t('uploadSheet.q.error'), 'err'] : i.state === 'duplicate' ? [t('uploadSheet.q.duplicate'), 'neutral'] : i.state === 'confirmed' ? [t('uploadSheet.q.confirmed'), 'ok'] : d?.status === 'ready' ? [t('uploadSheet.q.done'), 'ok'] : [t('uploadSheet.q.check'), 'warn'];
            return (
              <li key={i.key}>
                <button type="button" className="up-item" onClick={() => onPick(i.key)} disabled={i.state === 'confirmed'}>
                  <FileBadge ext={(i.name.split('.').pop() ?? '').slice(0, 4).toUpperCase()} />
                  <span className="ui-text"><strong>{i.name}</strong><span>{d?.fields?.totalGrossCents != null ? [euro(d.fields.totalGrossCents), d.vendor].filter(Boolean).join(' · ') : t('uploadSheet.size', { size: num(i.size / 1024 / 1024, 1) })}</span></span>
                  <Pill tone={tone}>{(i.state === 'processing' || i.state === 'uploading') && <Spinner size={12} />}{label}</Pill>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <input ref={input} type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.webp" onChange={(e) => { const x = [...(e.target.files ?? [])]; e.target.value = ''; if (x.length) onAdd(x); }} />
      <Button variant="dashed" icon="plus" onClick={() => input.current?.click()}>{t('uploadSheet.addMore')}</Button>
      <span className="small muted">{t('uploadSheet.keepWorking')}</span>
    </section>
  );
}
